import {
  ColumnDirective,
  ColumnsDirective,
  EditSettingsModel,
  GridComponent,
  Inject,
  Page,
  Sort,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../../../../core/styles/sortable-columns.css';

import { EstimateAndQuoteSend } from './EstimateAndQuoteSend';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { CustomSelectOption } from '@components/Form/CustomSelect';
import { OkButton } from '@components/Form/OkButton';
import { DynamicRecordFilter, TableActions } from '@components/Grid';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { PageContainer } from '@components/PageContainer';
import { routes } from '@config/routes';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import {
  EstimateStatus,
  estimateStatusSelectOptions,
} from '@models/EstimateAndQuote';
import { EstimateAndQuoteService } from '@services/EstimateAndQuote';

export const EstimateAndQuoteList: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [queryFilter, setQueryFilter] = useState<string>('');
  const [showDelete, setShowDelete] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState<number>(0);
  const [selectedEstimateDisplayString, setEstimateDisplayString] =
    useState<any>(0);
  const [customerFilterSelectOptions, setCustomerFilterSelectOptions] =
    useState<CustomSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCustomerFilterLoading, setIsCustomerFilterLoading] =
    useState<boolean>(true);
  const [showSend, setShowSend] = useState(false);

  // Create a service wrapper that matches useSyncfusionGrid interface
  const estimateServiceWrapper = {
    ...EstimateAndQuoteService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return EstimateAndQuoteService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
  } = useSyncfusionGrid({
    service: estimateServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  // Custom hooks for grid functionality
  const {
    filters,
    updateFilter,
    updateFilterAndApply,
    clearFilters,
    applyFilters,
  } = useGridFilters(
    {
      customerFilter: '',
      statusFilter: '',
      dateFromFilter: '',
      dateToFilter: '',
      estimateNumberFilter: '',
    },
    setQueryFilter
  );

  const {
    handleDelete: handleDeleteOperation,
    handleDuplicate: handleDuplicateOperation,
    handleApprove: handleApproveOperation,
    handleConvertToInvoice: handleConvertToInvoiceOperation,
  } = useGridOperations({
    service: EstimateAndQuoteService,
    refreshGrid: fetchData,
    entityName: 'estimate',
  });

  const editOptions: EditSettingsModel = {
    allowAdding: true,
    allowDeleting: true,
    allowEditing: true,
    mode: 'Dialog',
  };

  const loadCustomerFilterSelectOptions = () => {
    EstimateAndQuoteService.getCustomerFilterSelectOptions().then((result) => {
      if (result.isSuccess && 'data' in result) {
        const customerSelectOptions = result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }));
        setCustomerFilterSelectOptions(customerSelectOptions);
        setIsCustomerFilterLoading(false);
      }
    });
  };

  useEffect(() => {
    loadCustomerFilterSelectOptions();
  }, []);

  useEffect(() => {
    if (!isCustomerFilterLoading) {
      setIsLoading(false);
    }
  }, [isCustomerFilterLoading]);

  const handleSend = async (rowId: number, rowEstimateNumber: string) => {
    try {
      setSelectedEstimateId(rowId);
      setEstimateDisplayString(rowEstimateNumber);
      setShowSend(true);
    } catch (error) {
      const errorMessage = `Failed to handle send action for estimate ${rowEstimateNumber}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const handleDelete = async (rowId: number, rowEstimateNumber: string) => {
    try {
      setSelectedEstimateId(rowId);
      setEstimateDisplayString(rowEstimateNumber);
      setShowDelete(true);
    } catch (error) {
      const errorMessage = `Failed to handle delete action for estimate ${rowEstimateNumber}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const confirmDelete = () => {
    handleDeleteOperation(selectedEstimateId, selectedEstimateDisplayString);
    setShowDelete(false);
  };

  const handleApprove = (rowId: number, rowEstimateNumber: string) => {
    handleApproveOperation(rowId, rowEstimateNumber);
  };

  const handleView = (rowId: number) => {
    console.log('View', rowId);
    navigate(`${routes.RNC_ESTIMATES}/${rowId}`);
  };

  const handleEdit = (rowId: number) => {
    navigate(`${routes.RNC_ESTIMATES}/${rowId}/edit`);
  };

  const handleExportToPdf = (rowId: number) => {
    navigate(`${routes.RNC_ESTIMATES}/${rowId}/pdf`);
  };

  const handleDuplicate = (rowId: number) => {
    handleDuplicateOperation(rowId);
  };

  const actionButtons = (props: any) => {
    const dropdownActions = [
      {
        title: 'Edit',
        onClick: () => handleEdit(props.id),
        isHidden: props.status !== EstimateStatus.Draft,
      },
      {
        title: 'View',
        onClick: () => handleView(props.id),
      },
      {
        title: 'Duplicate',
        onClick: () => handleDuplicate(props.id),
      },
      {
        showTopSeparator: true,
        title: 'Send',
        onClick: () => handleSend(props.id, props.estimateNumber),
      },
      {
        title: 'Approve',
        onClick: () => handleApprove(props.id, props.estimateNumber),
        isHidden: props.status !== EstimateStatus.Draft,
      },
      {
        title: 'Convert to Invoice',
        onClick: () => {
          handleConvertToInvoiceOperation(props.id);
        },
        isHidden: props.status !== EstimateStatus.Approve,
      },
      {
        title: 'Export to PDF',
        onClick: () => handleExportToPdf(props.id),
      },
      {
        showTopSeparator: true,
        title: 'Delete',
        onClick: () => handleDelete(props.id, props.estimateNumber),
        isHidden: props.status === EstimateStatus.Invoiced,
      },
    ];

    return (
      <TableActions
        inlineActions={[
          {
            title: 'Send',
            onClick: () => handleSend(props.id, props.estimateNumber),
          },
        ]}
        dropdownActions={dropdownActions}
      />
    );
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <DynamicRecordFilter
          label="Filters"
          filters={[
            {
              label: 'Select Customer',
              type: 'select',
              selection: customerFilterSelectOptions,
              value: filters.customerFilter,
              onChange: (e) => {
                updateFilterAndApply('customerFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Select Status',
              type: 'select',
              selection: estimateStatusSelectOptions,
              value: filters.statusFilter,
              onChange: (e) => {
                updateFilterAndApply('statusFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Date From',
              type: 'date',
              value: filters.dateFromFilter,
              onChange: (e) => {
                updateFilterAndApply('dateFromFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Date To',
              type: 'date',
              value: filters.dateToFilter,
              onChange: (e) => {
                updateFilterAndApply('dateToFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Estimate Number',
              type: 'text',
              value: filters.estimateNumberFilter,
              onChange: (e) => {
                updateFilter('estimateNumberFilter', e.target.value);
              },
            },
          ]}
          onFilter={() => {
            applyFilters(() => fetchData(true));
          }}
          onClear={() => {
            clearFilters();
            applyFilters(() => fetchData(true));
          }}
        />

        <PageContainer>
          <GridComponent
            key={gridKey}
            dataBound={renderComplete}
            dataSource={gridData}
            ref={gridRef}
            allowPaging={true}
            allowSorting={true}
            pageSettings={{
              pageSize: 10,
              pageSizes: [5, 10, 20, 50, 100],
            }}
            editSettings={editOptions}
            dataStateChange={dataStateChange}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="statusDisplayString"
                headerText="Status"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="date"
                headerText="Date (MM/dd/yyyy)"
                customAttributes={{ class: 'sortable-column' }}
                template={(props: any) => {
                  return (
                    <span>
                      {new Date(props.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </span>
                  );
                }}
              />
              <ColumnDirective
                field="estimateNumber"
                headerText="Number"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="customerCompanyName"
                headerText="Customer"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="total"
                headerText="Amount"
                textAlign="Right"
                customAttributes={{ class: 'sortable-column' }}
                template={(props: any) => {
                  return (
                    <span>
                      {Number(props.total).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  );
                }}
              />
              <ColumnDirective
                headerText="Actions"
                width={140}
                allowSorting={false}
                customAttributes={{ class: 'non-sortable-column' }}
                template={actionButtons}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Toolbar]} />
          </GridComponent>

          {showDelete && (
            <>
              <Modal show={true}>
                <CloseButton onClick={() => setShowDelete(false)} />
                <Box size="medium">
                  <div className="px-4 pb-2">
                    <span>
                      Are you sure to delete Estimate:{' '}
                      {selectedEstimateDisplayString}
                    </span>
                  </div>
                  <FormButtonsContainer>
                    <div className="space-x-2">
                      <CancelButton onClick={() => setShowDelete(false)} />
                      <OkButton onClick={confirmDelete}>Delete</OkButton>
                    </div>
                  </FormButtonsContainer>
                </Box>
              </Modal>
            </>
          )}

          {showSend && (
            <>
              <Modal show={true}>
                <EstimateAndQuoteSend
                  onDownload={() => handleExportToPdf(selectedEstimateId)}
                  estimateId={selectedEstimateId}
                  onClose={(refreshGrid?: boolean) => {
                    setShowSend(false);
                    if (refreshGrid) {
                      fetchData(true);
                    }
                  }}
                  estimateName={selectedEstimateDisplayString}
                />
              </Modal>
            </>
          )}
        </PageContainer>
      </>
    );
  }
};
